/**
 * Utility to safely convert API error responses to human-readable strings.
 * Handles FastAPI/Pydantic validation errors (422) and other error formats.
 */

/**
 * Extracts a human-readable error message from various API error formats.
 * @param {any} error - Axios error object or any error
 * @param {string} fallback - Fallback message if extraction fails
 * @returns {string} Human-readable error message
 */
export const formatApiError = (error, fallback = "Something went wrong. Please try again.") => {
  try {
    // Handle Axios error structure
    const data = error?.response?.data;
    
    if (!data) {
      // No response data - might be network error
      if (error?.message) {
        return error.message;
      }
      return fallback;
    }

    // Case 1: Simple string detail (FastAPI HTTPException)
    if (typeof data.detail === "string") {
      return data.detail;
    }

    // Case 2: Pydantic validation errors (422) - array of error objects
    // Format: { detail: [{ type, loc, msg, input, ctx }, ...] }
    if (Array.isArray(data.detail)) {
      const messages = data.detail.map((err) => {
        // Extract field location (last item in loc array is usually the field name)
        const field = Array.isArray(err.loc) 
          ? err.loc[err.loc.length - 1] 
          : "field";
        const msg = err.msg || "invalid value";
        return `${field}: ${msg}`;
      });
      return messages.join(", ");
    }

    // Case 3: Object detail (shouldn't happen often, but handle it)
    if (typeof data.detail === "object" && data.detail !== null) {
      // Try to extract msg property
      if (data.detail.msg) {
        return data.detail.msg;
      }
      // Fallback to stringifying
      return JSON.stringify(data.detail);
    }

    // Case 4: Direct message property
    if (typeof data.message === "string") {
      return data.message;
    }

    // Case 5: Direct error property
    if (typeof data.error === "string") {
      return data.error;
    }

    // Case 6: Data is a string itself
    if (typeof data === "string") {
      return data;
    }

    return fallback;
  } catch (e) {
    console.error("Error formatting API error:", e);
    return fallback;
  }
};

/**
 * Checks if a value is safe to render in React (not an object/array)
 * @param {any} value - Value to check
 * @returns {boolean}
 */
export const isSafeToRender = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return true;
  return false;
};

/**
 * Safely converts any value to a renderable string
 * @param {any} value - Value to convert
 * @param {string} fallback - Fallback if conversion fails
 * @returns {string}
 */
export const safeString = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

export default formatApiError;
